import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ConsentValue = "granted" | "denied" | null;

const STORAGE_KEY = "consent_marketing";
const EVENT_NAME = "consent-change";
export const CONSENT_NOTICE_VERSION = "2026-09-28";

// EEE + Reino Unido + Suíça: exigem aceite prévio para qualquer tag de anúncio.
const STRICT = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL",
  "PL","PT","RO","SK","SI","ES","SE","IS","LI","NO","GB","CH",
]);

let regionPromise: Promise<{ country: string; strict: boolean }> | null = null;

/** País do visitante via /cdn-cgi/trace (cache por visita). Falha = região rígida. */
export function getRegion(): Promise<{ country: string; strict: boolean }> {
  if (typeof window === "undefined") return Promise.resolve({ country: "XX", strict: true });
  if (!regionPromise) {
    regionPromise = (async () => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 2000);
        const res = await fetch("/cdn-cgi/trace", { signal: ctrl.signal });
        clearTimeout(t);
        if (!res.ok) throw new Error("trace");
        const loc = /(?:^|\n)loc=([A-Z0-9]{2})/.exec(await res.text())?.[1] ?? "XX";
        return { country: loc, strict: loc === "XX" || loc === "T1" || STRICT.has(loc) };
      } catch {
        return { country: "XX", strict: true };
      }
    })();
  }
  return regionPromise;
}

export function getConsent(): ConsentValue {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "granted" || v === "denied") return v;
  return null;
}

function pushGoogleConsent(value: ConsentValue) {
  // Sem escolha: mantém o padrão regional definido no <head>.
  if (value === null) return;
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  const s = value === "granted" ? "granted" : "denied";
  w.gtag?.("consent", "update", {
    ad_storage: s, ad_user_data: s, ad_personalization: s, analytics_storage: s,
  });
}

function logChoice(choice: string) {
  void getRegion().then(({ country }) =>
    (supabase as any)
      .from("consent_events")
      .insert({ choice, country, notice_version: CONSENT_NOTICE_VERSION })
      .then(() => undefined, () => undefined),
  );
}

export function setConsent(value: Exclude<ConsentValue, null>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, value);
  window.localStorage.setItem(`${STORAGE_KEY}_at`, new Date().toISOString());
  window.localStorage.setItem(`${STORAGE_KEY}_version`, CONSENT_NOTICE_VERSION);
  pushGoogleConsent(value);
  logChoice(value);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
}

/** Registra (anônimo) que o banner foi exibido, para medir quem ignora. */
export function logBannerShown() {
  if (typeof window === "undefined") return;
  if (sessionStorage.getItem("consent_shown_logged")) return;
  sessionStorage.setItem("consent_shown_logged", "1");
  logChoice("shown");
}

export function resetConsent() {
  if (typeof window === "undefined") return;
  // Reabrir preferências mantém a escolha atual até o visitante mudar.
  window.dispatchEvent(new CustomEvent("consent-open"));
}

export function useConsent(): ConsentValue {
  const [consent, setLocal] = useState<ConsentValue>(null);

  useEffect(() => {
    const handler = () => {
      setLocal(getConsent());
      pushGoogleConsent(getConsent());
    };
    setLocal(getConsent());
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return consent;
}

/** Anúncios: com aceite = personalizados; fora de região rígida = não personalizados. */
export function useAdsMode(): "personalized" | "non-personalized" | "blocked" | "pending" {
  const consent = useConsent();
  const [strict, setStrict] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    getRegion().then((r) => alive && setStrict(r.strict));
    return () => { alive = false; };
  }, []);
  if (consent === "granted") return "personalized";
  if (strict === null) return "pending";
  return strict ? "blocked" : "non-personalized";
}
